<template>
  <div data-view="promotions-deals">
    <Can
      action="view-business-contact"
      subject="ad-manager.order"
    >
      <dialog-empty :is-on="isAddNewOrderStepperOn" :on-close="toggleNewOrderStepper">
        <template v-slot:content>
          <AddNewOrderStepper
            ref="promostepper"
            :flow="promotionFlow"
            :promoted-deal="selectedDeal"
            :on-cancel="toggleNewOrderStepper"
            :on-confirm="createOrder"
            :on-update="updateOrder"
            :on-create-li="createLineItem"
            :select-on-create="() => {}"
          />
        </template>
      </dialog-empty>
    </Can>

    <layout-logged-frame title="Promotable deals">
      <template v-slot:header-actions>
        <v-btn
          color="primary"
          :disabled="!hasEnabledForPromotionCountries"
          @click="openNewOrderStepper"
        >
          <v-icon>add</v-icon>
          {{ $t('new_promotion') }}
        </v-btn>
      </template>

      <template v-slot:content>
        <PromotionsAlerts
          :incomplete="incompleteBilling"
          :pending="pendingBilling"
          :denied="deniedBilling"
        />

        <PromotionsDealsFilter
          ref="dealsfilter"
          :is-data-loaded="isDataLoaded"
          :is-skeleton-loading="isSkeletonLoading"
          :placements-types="placementsTypes"
          @countryChanged="onCountryChanged"
          @filter="onFilter"
          @clearFilters="onClearFilters"
        />

        <div
          v-if="isLoadingThreads"
          class="threads-table-empty"
        >
          <v-progress-circular
            color="primary"
            indeterminate
            :width="3"
            :size="34"
          />
        </div>

        <div
          v-else-if="hasThreads"
          class="threads-table"
        >
          <v-list>
            <v-list-item
              v-for="deal in threads"
              :value="deal.id"
              :key="deal.id"
              class="thread-item"
            >
              <div class="thread-social">
                <p :class="tempRatingColor(deal.temperature_level)">
                  {{ deal.temperature_rating }}°
                </p>
                <p>
                  {{ extractCommentCount(deal.comment_count) }}
                </p>
              </div>
              <div
                :style="{ backgroundImage: `url('${deal.image || 'https://www.westernillinoisrealty.com/img/noimage.png'}')` }"
                class="thread-image"
              />
              <div class="thread-details">
                <p class="thread-details-title">
                  {{ deal.title }}
                </p>
                <div class="thread-details-price">
                  <p>{{ deal.price_display }}</p>
                  <p>{{ extractCommitedDate(deal.submitted) }}</p>
                </div>
                <div class="thread-details-general">
                  <div class="thread-details-item">
                    <v-icon size="18" class="material-icons-outlined">
                      store
                    </v-icon>
                    <TruncatedString :deal="extractMerchantName(deal)" :maxLength="19"/>
                  </div>
                  <div class="thread-details-item">
                    <v-icon size="18" class="material-icons-outlined">
                      sell
                    </v-icon>
                    {{ deal.category }}
                  </div>
                  <div
                    class="thread-details-item"
                    @click="openInNewTab(deal)"
                  >
                    <v-icon size="18">
                      link
                    </v-icon>
                    {{ $t('view_deal') }}
                  </div>
                </div>
              </div>
              <div class="thread-platform">
                {{ deal?.country?.platform }}
              </div>
              <v-btn
                :id="deal.id"
                variant="outlined"
                rounded
                color="primary"
                :disabled="isAddBtnDisabled"
                @click="promoteDeal"
              >
                <v-icon>add</v-icon>
                {{ $t('promote') }}
              </v-btn>
            </v-list-item>
          </v-list>
        </div>

        <div
          v-if="!isLoadingThreads && isFullResponse"
          class="threads-load-more"
        >
          <v-btn
            v-if="loadMore"
            read-only
          >
            <v-progress-circular
              class="spinner"
              color="secondary"
              indeterminate
              :width="2"
              :size="16"
            />
            {{ $t('loading_more_items') }}
          </v-btn>
          <v-btn
            v-else
            @click="getNextPage"
          >
            {{ $t('show_more') }}
          </v-btn>
        </div>

        <div v-if="!isLoadingThreads && !hasThreads">
          <no-data class="mt-6" :title="$t('no_data_available')"/>
        </div>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import PromotionsDealsFilter from '../../components/forms/filters/PromotionsDeals';
  import AddNewOrderStepper from '../../components/steppers/Promotion';
  import TruncatedString from '../../components/forms/line/TruncatedString';
  import PromotionsAlerts from '../../components/alerts/Promotions';
  import { isNonEmptyStr, isObj, isStr, openUrlInNewWindow } from '@/utils';

  export default {
    components: {
      PromotionsDealsFilter,
      AddNewOrderStepper,
      TruncatedString,
      PromotionsAlerts,
    },
    data() {
      return {
        activeCountry: '',
        selectedDeal: {},
        selectedPlacementId: null,
        isSkeletonLoading: true,
        merchantId: '',
        merchantIds: [],
        placementsTypes: [],
        query: {
          page: 1
        },
        pagination: {},
        selectedOrder: {},
        createdOrder: {},
        promotionFlow: '',
        loadMore: false,
        isLoading: false,
        isLoadingThreads: true,
        hasLoadedMerchants: false,
        hasLoadedPlacementsTypes: false,
        isAddNewOrderModalOn: false,
        isEditOrderModalOn: false,
        isAddNewOrderStepperOn: false,
        isAddingOrder: false,
        isEditingOrder: false,
        canEditDetails: false,
        isAddingLineItem: false,
        isLoadingOrder: false
      };
    },
    computed: {
      disabledForPromotionCountries() {
        const { disabledForPromotionCountries } = this.$store.getters['businesses/activeItem'] || {};
        return disabledForPromotionCountries || [];
      },
      enabledPromoCountries() {
        return this.$store.getters['businessUsers/enabledPromoCountries'];
      },
      hasEnabledForPromotionCountries() {
        return this.disabledForPromotionCountries.length !== this.enabledPromoCountries.length;
      },
      activeUser() {
        return this.$store.getters['auth/user'];
      },
      billingCountriesByStatus() {
        const { billingCountriesByStatus } = this.$store.getters['businesses/activeItem'];
        return billingCountriesByStatus || [];
      },
      threads() {
        return this.$store.getters['promotions/threads'];
      },
      canUpdateOrder() {
        return this.$ability.can('update', 'ad-manager.order');
      },
      hasThreads() {
        return this.threads.length;
      },
      isFullResponse() {
        return this.hasThreads && !(this.hasThreads % 50);
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      incompleteBilling() {
        return this.alertMessageBaseCondition('no_billing');
      },
      pendingBilling() {
        return this.alertMessageBaseCondition('networks_in_review');
      },
      deniedBilling() {
        return this.alertMessageBaseCondition('networks_denied');
      },
      isCountryAllowedForPromotion() {
        return -1 === this.disabledForPromotionCountries.findIndex(country => country.code === this.activeCountry) &&
          -1 !== this.enabledPromoCountries.findIndex(country => country.code === this.activeCountry);
      },
      isAddBtnDisabled() {
        return this.isAddingLineItem ||
          this.isLoadingOrder ||
          !this.isCountryAllowedForPromotion;
      },
      isDataLoaded() {
        return this.hasLoadedMerchants && this.hasLoadedPlacementsTypes;
      }
    },
    watch: {
      activeCountry(nextCountry, prevCountry) {
        const { isLoadingThreads, getBusinessMerchants, getPlacementsTypes } = this;

        // checking if isLoadingThreads is false to avoid multiple calls in first load
        // in other words, if isLoadingThreads is true, it means that the first load is still ongoing
        if (!isLoadingThreads && nextCountry.length > 0 && nextCountry !== prevCountry) {
          this.hasLoadedMerchants = false;
          this.hasLoadedPlacementsTypes = false;

          getBusinessMerchants()
          getPlacementsTypes()
        }
      },
    },
    created() {
      const { getBillingCountriesByStatus, getEnabledCountriesForPromotion } = this;

      getBillingCountriesByStatus(); // does not depend on active country

      getEnabledCountriesForPromotion()
          .then(countries => {
            // Don't stomp a country the filter form already restored from
            // the URL (via onFilter(), called synchronously in its own
            // created(), which runs before this promise resolves).
            if (!this.activeCountry) {
              this.activeCountry = countries[0].code;
            }
            this.getInitialData();
          })
          .catch(error => error);
    },
    methods: {
      onCountryChanged(country) {
        this.activeCountry = country;
      },
      async getInitialData(country) {
        return Promise.all([this.getBusinessMerchants(), this.getPlacementsTypes()]);
      },
      getEnabledCountriesForPromotion() {
        return this.$store.dispatch('businessUsers/getEnabledCountriesForPromotion');
      },
      getThreads() {
        if (!this.hasLoadedMerchants || !this.hasLoadedPlacementsTypes || !isNonEmptyStr(this.activeCountry)) {
          return;
        }

        const query = {
          ...this.query,
          placement_id: this.selectedPlacementId,
          merchant_id: this.merchantId,
          merchant_ids: this.merchantIds,
          country: this.activeCountry
        };

        this.isLoadingThreads = !this.loadMore;

        return this.$store.dispatch('promotions/getThreads', query)
          .then(({ threads }) => {
            this.isSkeletonLoading = false;
            this.isLoadingThreads = false;
            this.loadMore = false;
            return threads;
          })
          .catch((err) => {
            this.isSkeletonLoading = false;
            this.isLoadingThreads = false;
            this.loadMore = false;
            return err;
          });
      },
      async getPlacementsTypes() {
        if (isNonEmptyStr(this.activeCountry)) {
          const query = {
            country: this.activeCountry
          };

          return this.$store.dispatch('promotions/getPlacementsTypes', query)
            .then(res => {
              this.placementsTypes = res;
              this.selectPlacementId();
              this.hasLoadedPlacementsTypes = true;

              return res.placements;
            })
            .catch(err => {
              this.hasLoadedPlacementsTypes = true;
              return err;
            });
        }
      },
      getBillingCountriesByStatus() {
        return this.$store.dispatch('businesses/getBillingCountriesByStatus');
      },
      getNextPage() {
        this.query.page += 1;
        this.loadMore = true;

        this.getThreads();
      },
      promoteDeal(e) {
        if (isObj(e) && isStr(e.currentTarget.id)) {
          const selectedId = Number(e.currentTarget.id);
          this.selectedDeal = this.threads.find(({ id }) => id === selectedId);

          this.promotionFlow = 'promote-deal';

          this.toggleNewOrderStepper();
        }
      },
      async getBusinessMerchants() {
        if (isNonEmptyStr(this.activeCountry) && isObj(this.activeUser.business)) {
          const query = {
            country: this.activeCountry,
          };

          return this.$store.dispatch('promotions/getBusinessMerchants', query)
            .then(res => {
              this.hasLoadedMerchants = true;
              return res;
            })
            .catch(err => {
              this.hasLoadedMerchants = true;
              return err;
            });
        }

        this.hasLoadedMerchants = true;
      },
      onFilter(filters) {
        this.query = this.getSearchQuery(filters);
        this.query.page = 1;
        this.selectedPlacementId = filters.placement_id;
        this.merchantId = filters.merchant_id;
        this.merchantIds = filters.merchant_ids;

        // Normally the filter form's own @countryChanged emit keeps this in
        // sync (see onCountryChanged below) — but that emit is skipped while
        // isSkeletonLoading is true, which is exactly the window a URL
        // restore's synchronous onFilter() call lands in. Setting it here
        // too covers that case; it's a no-op once the emit has already run.
        if (filters.country) {
          this.activeCountry = filters.country;
        }

        if (!this.isDataLoaded) {
          setTimeout(() => {
            this.onFilter(filters);
          }, 100);
        } else {
          this.getThreads();
        }
      },
      onClearFilters() {
        this.selectPlacementId();
        this.query.page = 1;
      },
      selectPlacementId() {
        let placementId;

        if (this.placementsTypes.length === 1) {
          placementId = this.placementsTypes[0].id;
        } else {
          // eslint-disable-next-line array-callback-return
          this.placementsTypes.some(i => {
            if (i.key === 'HOT') {
              placementId = i.id;
              return true;
            }
          });
        }

        this.selectedPlacementId = placementId;
      },
      getSearchQuery({ placement_id, merchant_id, merchant_ids, group_id, thread_id }) {
        const query = {};

        query.merchant_ids = merchant_ids;
        query.merchant_id = merchant_id;
        query.group_id = group_id;
        query.thread_id = thread_id;
        query.placement_id = placement_id;
        query.page = this.query.page;

        return query;
      },
      toggleNewOrderModal() {
        this.isAddNewOrderModalOn = !this.isAddNewOrderModalOn;
      },
      toggleNewOrderStepper() {
        if (this.isAddNewOrderStepperOn) {
          this.onClearFilters();
          this.selectedDeal = {};

          if (this.$refs.promostepper && this.$refs.promostepper.$refs.addnewlikb) {
            this.$refs.promostepper.$refs.addnewlikb.clear();
          }
        }

        this.isAddNewOrderStepperOn = !this.isAddNewOrderStepperOn;
      },
      openNewOrderStepper() {
        this.promotionFlow = 'new-promotion';
        this.toggleNewOrderStepper();
      },
      createLineItem(lineItem) {
        return this.$store.dispatch('promotions/createLineItem', lineItem)
          .then((res) => {
            if (res.success) {
              this.$store.dispatch('promotions/getOrder', lineItem.order_id);
            }
            this.getThreads();
            return res;
          });
      },
      createOrder(order) {
        this.isAddingOrder = true;

        return this.$store.dispatch('promotions/createOrder', order)
          .then(res => {
            this.isAddingOrder = false;

            if (res.success) {
              this.createdOrder = res.data.data.data;
            }

            return res;
          });
      },
      async updateOrder(order) {
        if (!this.canUpdateOrder) {
          return undefined;
        }

        const nextOrder = {
          ...order,
          id: this.selectedOrder.id || this.createdOrder.id,
          isBC: true
        };

        this.isEditingOrder = true;

        return this.$store.dispatch('promotions/updateOrder', nextOrder)
          .then(res => {
            this.createdOrder = res.data.data;
            return res;
          })
          .catch(err => err)
          .finally(() => {
            this.isEditingOrder = false;
          });
      },
      selectOrder(order) {
        this.selectedOrder = order;
        this.isEditOrderModalOn = true;

        return this.$store.dispatch('promotions/setActiveOrder', order);
      },
      openInNewTab(deal) {
        if (deal.url) {
          openUrlInNewWindow(window.open(), deal.url);
        }
      },
      tempRatingColor(value) {
        const colors = {
          hot1: 'red-color',
          hot2: 'orange-color',
          hot3: 'green-color',
          cold1: 'light-blue-color',
          cold2: 'blue-color',
          cold3: 'blue-color',
        };

        const color = colors[value];

        return color || 'black-color';
      },
      extractCommentCount(count) {
        if (!count) {
          return 'No Comments';
        }

        const text = count === 1 ? 'Comment' : 'Comments';

        return `${count} ${text}`;
      },
      extractCommitedDate(commitedDate) {
        return this.$dayjs(commitedDate).fromNow();
      },
      extractMerchantName(deal) {
        if (!deal || !isObj(deal) || !isObj(deal.merchant) || !isStr(deal.merchant.name)) {
          return 'N/A';
        }

        return deal.merchant.name;
      },
      alertMessageBaseCondition(arrayName) {
        const { billingCountriesByStatus } = this;

        // eslint-disable-next-line max-len
        if ( billingCountriesByStatus && billingCountriesByStatus[arrayName] && billingCountriesByStatus[arrayName].length) {
          return billingCountriesByStatus[arrayName];
        }

        return [];
      },
    }
  };
</script>

<style lang="scss">
  [data-view="promotions-deals"] {
    height: 100%;

    .promotions-deals-header {
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .v-btn {
        border-radius: 4px;

        &:disabled {
          color: white !important;

          .v-icon {
            color: white !important;
          }
        }
      }
    }

    .thread-platform {
      white-space: nowrap;
      font-size: 14px;
      align-self: flex-start;
      margin-right: 140px;
    }

    .threads-table {
      margin: 24px 0;
      border-radius: 8px;
      overflow: hidden;

      .thread-item {
        border-bottom: 1px solid #E0E0E0;
        padding: 0;

        .v-list-item__content {
          display: flex;
          align-items: center;
          padding: 20px;
          font-size: 14px;
          justify-content: space-between;
        }

        &:last-child {
          border: none;
        }

        .thread-social {
          min-width: 86px;
          height: 90px;

          p {
            margin: 0;
            padding: 0;
            font-size: 12px;
            color: #757575;

            .v-theme--dark & {
              color: rgba(var(--v-theme-on-surface), 0.6);
            }

            &:first-child {
              margin-bottom: 10px;
              font-size: 14px;
              font-weight: 600;
            }

            &:last-of-type {
              margin-bottom: 20px;
            }
          }

          span {
            padding: 2px 10px;
            font-size: 12px;
            border-radius: 28px;
          }

          .green-color {
            color: #43A047;
          }

          .orange-color {
            color: orange;
          }

          .red-color {
            color: #F44336;
          }

          .light-blue-color {
            color: #72BCD4;
          }

          .blue-color {
            color: blue;
          }

          .black-color {
            color: black;
          }
        }

        .thread-image {
          margin: 0 20px;
          width: 90px;
          height: 90px;
          min-width: 90px;
          min-height: 90px;
          border-radius: 8px;
          background-size: cover;
          background-position: center;
        }

        .thread-details {
          margin-right: 20px;
          width: 100%;
          height: 90px;
          font-size: 12px;
          font-weight: 600;

          p {
            margin: 0;
            padding: 0;
          }

          .thread-details-title {
            font-size: 14px;
          }

          .thread-details-price {
            margin: 10px 0;

            p {
              display: inline-block;
              font-size: 12px;
              width: 150px;

              &:last-child {
                margin-left: 15px;
                color: #9E9E9E;
              }
            }
          }

          .thread-details-general {
            display: flex;
            align-items: center;
            color: #757575;

            .v-theme--dark & {
              color: rgba(var(--v-theme-on-surface), 0.6);
            }

            div {
              display: inline;

              .v-icon {
                padding: 0 3px 2px 0;
              }
            }

            .thread-details-item {
              &:first-child {
                width: 150px;
              }

              &:nth-child(2) {
                margin: 0 15px;
              }

              &:last-child {
                cursor: pointer;
              }
            }
          }
        }
      }
    }

    .threads-load-more {
      text-align: center;

      .v-btn {
        .spinner {
          margin-right: 12px;
        }
      }
    }

    .threads-table-empty {
      margin-top: 24px;
      height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: grey;
      border-radius: 2px;
    }
  }
</style>
